"use client";

import { useState } from "react";
import { RequestInfo } from "rwsdk/worker";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Zap,
  ArrowRight,
  Check,
  Mail,
  BarChart3,
  Eye,
  Lock,
  ChevronDown,
  ArrowUpRight,
} from "lucide-react";
import { AppLayout } from "@/components/layouts";

function FAQItem({
  question,
  answer,
  defaultOpen = false,
}: {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full py-6 text-left cursor-pointer"
      >
        <span className="font-medium text-[15px] pr-8">{question}</span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-all duration-200 ease-in-out ${open ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">
          <p className="text-muted-foreground text-sm leading-relaxed">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export function Landing({ ctx }: RequestInfo) {
  return (
    <AppLayout ctx={ctx}>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-60" />
        <div className="relative container mx-auto px-4 pt-20 pb-24 sm:pt-32 sm:pb-36">
          <div className="max-w-2xl">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6 tracking-tight">
              Stop typing expenses.{" "}
              <span className="font-display italic text-muted-foreground">
                Start forwarding them.
              </span>
            </h1>

            <p className="text-lg text-muted-foreground mb-10 max-w-lg leading-relaxed">
              Forward receipt emails to your private address. AI extracts the
              details in minutes. No spreadsheets, no bank connections, no data
              harvesting.
            </p>

            <div className="flex items-center gap-4 flex-wrap">
              <Button
                size="lg"
                asChild
                className="h-12 px-7 text-[15px] rounded-full"
              >
                <a
                  href={ctx.user ? "/dashboard" : "/user/login"}
                  className="inline-flex items-center"
                >
                  {ctx.user ? "Go to Dashboard" : "Get Started — Free"}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
              <span className="text-sm text-muted-foreground">
                No credit card needed
              </span>
            </div>
          </div>

          {/* Visual element — email flow diagram */}
          <div className="hidden lg:block absolute right-12 xl:right-24 top-1/2 -translate-y-1/2">
            <div className="flex items-center gap-4 text-sm">
              <div className="w-48 border rounded-xl p-4 bg-card shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-red-400" />
                  <div className="w-2 h-2 rounded-full bg-yellow-400" />
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium text-muted-foreground">
                    From: amazon@email.com
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Your order of...
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <div className="w-6 h-7 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <span className="text-[8px] font-bold text-red-600 dark:text-red-400">
                        PDF
                      </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      receipt.pdf
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center gap-1">
                <ArrowRight className="w-5 h-5 text-muted-foreground/50" />
                <span className="text-[10px] text-muted-foreground/50 font-medium">
                  forward
                </span>
              </div>

              <div className="w-48 border rounded-xl p-4 bg-card shadow-sm">
                <div className="text-xs font-mono text-muted-foreground mb-3">
                  your-id@fc.app
                </div>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Vendor</span>
                    <span className="font-medium">Amazon</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-medium">$42.99</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Date</span>
                    <span className="font-medium">Mar 21</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Category</span>
                    <span className="font-medium">Shopping</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="border-t bg-muted/30 dark:bg-muted/10">
        <div className="container mx-auto px-4 py-20 sm:py-28">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-16 items-start">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
                How it works
              </p>
              <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-4">
                Three steps.
                <br />
                Zero data entry.
              </h2>
              <p className="text-muted-foreground">
                Most expense tools ask you to photograph receipts, fill out
                forms, or connect your bank. ForwardCents asks you to do one
                thing: forward an email.
              </p>
            </div>

            <div className="space-y-0">
              {[
                {
                  num: "01",
                  icon: Mail,
                  title: "Get your private email address",
                  desc: "Sign up and instantly receive a unique address like you@forwardcents.app. This is your personal drop-box — only you know it, only you use it.",
                },
                {
                  num: "02",
                  icon: ArrowRight,
                  title: "Forward your receipts",
                  desc: "Get a receipt from Amazon, Uber, or your favorite coffee shop? Forward the email to your address. That's it. No apps to open, no photos to take.",
                },
                {
                  num: "03",
                  icon: BarChart3,
                  title: "Review your dashboard",
                  desc: "Everything lands in a clean dashboard. Review, edit, categorize, and export — your expenses are finally organized.",
                },
              ].map((step, i) => (
                <div key={i} className="grid grid-cols-[auto_1fr] gap-6 group">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full border-2 border-foreground/20 flex items-center justify-center text-xs font-bold text-muted-foreground group-hover:border-foreground/40 transition-colors">
                      {step.num}
                    </div>
                    {i < 2 && <div className="w-px flex-1 bg-border my-2" />}
                  </div>
                  <div className={`${i < 2 ? "pb-10" : ""}`}>
                    <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features — asymmetric layout, not a uniform grid */}
      <section className="border-t">
        <div className="container mx-auto px-4 py-20 sm:py-28">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
              Why ForwardCents
            </p>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight">
              Built for people who care about where their data goes.
            </h2>
          </div>

          {/* Bento-ish layout */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Large feature card */}
            <div className="sm:col-span-2 lg:col-span-2 border rounded-2xl p-8 sm:p-10 bg-muted/30 dark:bg-muted/10">
              <div className="flex items-start justify-between mb-8">
                <Shield className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                <Badge variant="secondary" className="text-xs">
                  Core principle
                </Badge>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl mb-3">
                Privacy by design, not by afterthought
              </h3>
              <p className="text-muted-foreground leading-relaxed max-w-lg">
                We remove personally identifiable information before processing.
                Your name, email, and address are stripped from receipt data
                before our AI ever sees it. Your financial data is for your eyes
                only.
              </p>
            </div>

            <div className="border rounded-2xl p-8 flex flex-col justify-between">
              <Zap className="w-6 h-6 text-amber-600 dark:text-amber-400 mb-8" />
              <div>
                <h3 className="font-semibold text-lg mb-2">AI extraction</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Extracts vendor, amount, date, and category from common
                  receipt formats in under 3 minutes. No manual entry.
                </p>
              </div>
            </div>

            <div className="border rounded-2xl p-8 flex flex-col justify-between">
              <Eye className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-8" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Simple dashboard</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  A clean interface to view, edit, and understand your spending.
                  Nothing more, nothing less.
                </p>
              </div>
            </div>

            <div className="sm:col-span-2 lg:col-span-1 border rounded-2xl p-8 flex flex-col justify-between">
              <Lock className="w-6 h-6 text-violet-600 dark:text-violet-400 mb-8" />
              <div>
                <h3 className="font-semibold text-lg mb-2">
                  You're in control
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Only emails you forward are processed. No bank connections. No
                  inbox scanning. No surprises.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy pledge — full-width dark band */}
      <section className="bg-foreground text-background">
        <div className="container mx-auto px-4 py-20 sm:py-28">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-6">
                Your financial data is not a product.
              </h2>
              <p className="text-background/60 text-lg mb-10 leading-relaxed">
                ForwardCents' approach is simple: provide a great tool that
                respects your privacy for a fair price. That's it.
              </p>
              <div className="space-y-4">
                {[
                  "Your data will never be sold",
                  "PII is automatically stripped before processing",
                  "No bank account connections — ever",
                  "Delete all your data at any time",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-background/10 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-background/80 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="w-full max-w-sm border border-background/10 rounded-2xl p-8 bg-background/5 backdrop-blur-sm">
                <p className="font-display text-2xl mb-2 text-center">
                  Built for
                </p>
                <p className="text-background/50 text-sm text-center mb-8">
                  Freelancers. Consultants. Side hustlers. Anyone who believes
                  their financial data should stay private.
                </p>
                <Button
                  size="lg"
                  className="w-full h-12 rounded-full bg-background text-foreground hover:bg-background/90"
                  asChild
                >
                  <a href="/user/login">Get your private email address</a>
                </Button>
                <p className="text-xs text-background/40 text-center mt-4">
                  Free plan. No credit card.
                </p>
                <a
                  href="/security"
                  className="block text-xs text-background/40 text-center mt-2 underline hover:text-background/60 transition-colors"
                >
                  Learn more about our security
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="container mx-auto px-4 py-20 sm:py-28">
        <div className="max-w-2xl mb-16">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
            Pricing
          </p>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-4">
            Simple pricing. No surprises.
          </h2>
          <p className="text-muted-foreground text-lg">
            Start free. Upgrade when you need more.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Free */}
          <div className="border rounded-2xl p-8">
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-1">Free</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">$0</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                For light expense tracking
              </p>
            </div>
            <div className="space-y-3 mb-8">
              {[
                "5 expenses per month",
                "AI data extraction",
                "Private email address",
                "Basic dashboard",
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <Button variant="outline" className="w-full rounded-full" asChild>
              <a href="/user/login">Start tracking free</a>
            </Button>
          </div>

          {/* Pro */}
          <div className="border-2 border-foreground rounded-2xl p-8 relative">
            <div className="absolute -top-3 right-6">
              <span className="bg-foreground text-background text-xs font-medium px-3 py-1 rounded-full">
                Popular
              </span>
            </div>
            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-1">Pro</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">$5</span>
                <span className="text-muted-foreground">/mo</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                For freelancers who track everything
              </p>
            </div>
            <div className="space-y-3 mb-8">
              {[
                { text: "Unlimited expenses", bold: true },
                { text: "AI data extraction" },
                { text: "Private email address" },
                { text: "Enhanced dashboard" },
                { text: "Edit expenses inline" },
                { text: "Export to CSV, Excel, PDF" },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 shrink-0" />
                  <span>
                    {f.bold ? <strong>{f.text}</strong> : f.text}
                    {f.badge && (
                      <span className="ml-2 text-[10px] text-muted-foreground border rounded-full px-2 py-0.5">
                        {f.badge}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
            <Button className="w-full rounded-full" asChild>
              <a href="/user/login">Upgrade to Pro</a>
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-3">
              Upgrade from your dashboard anytime.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t bg-muted/30 dark:bg-muted/10">
        <div className="container mx-auto px-4 py-20 sm:py-28">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-16">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
                FAQ
              </p>
              <h2 className="font-display text-3xl leading-tight">
                Common questions
              </h2>
            </div>

            <div>
              <FAQItem
                question="Do you connect to my bank account?"
                answer="No. We believe connecting to your bank is a privacy risk. You are in full control by only forwarding the specific expense emails you want to track."
                defaultOpen
              />
              <FAQItem
                question="How does the email forwarding work?"
                answer="You get a unique email address when you sign up. Simply forward any receipt email to this address, and we'll automatically process it and extract the expense details."
              />
              <FAQItem
                question="What happens to my emails after processing?"
                answer="The email is used to extract the expense data and then it is discarded. We do not store the original email content. Only the structured data (vendor, amount, date) is kept in our secure database."
              />
              <FAQItem
                question="What types of expenses can you track?"
                answer="Any email receipt with PDF attachments — Amazon purchases, Uber rides, restaurant bills, hotel bookings, office supplies, software subscriptions, and more. Works with Gmail, Outlook, Apple Mail, and all major email providers."
              />
              <FAQItem
                question="How accurate is the AI extraction?"
                answer="Our AI is quite accurate for most common digital receipts, but you can easily edit any details that need correction. We're continuously improving accuracy, and you always have full control over your expense data."
              />
              <FAQItem
                question="How long does processing take?"
                answer="Most receipts are processed within 1-3 minutes of forwarding the email. You'll see the extracted expense appear in your dashboard, where you can review and edit if needed."
              />
              <FAQItem
                question="What if something is extracted incorrectly?"
                answer="No problem! You can easily edit any expense details directly from your dashboard. Think of the AI as a helpful first pass that gets you 90% of the way there, saving you from typing everything manually."
              />
              <FAQItem
                question="How is ForwardCents different from Expensify or SAP Concur?"
                answer="Those tools are built for enterprises with complex approval workflows, team management, and corporate card integrations. ForwardCents is built for individuals — freelancers, consultants, and side hustlers who want simple, private expense tracking without connecting bank accounts or installing mobile apps."
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Structured Data for Rich Results — hardcoded content, no user input */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Do you connect to my bank account?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. We believe connecting to your bank is a privacy risk. You are in full control by only forwarding the specific expense emails you want to track.",
                },
              },
              {
                "@type": "Question",
                name: "How does the email forwarding work?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "You get a unique email address when you sign up. Simply forward any receipt email to this address, and we'll automatically process it and extract the expense details.",
                },
              },
              {
                "@type": "Question",
                name: "What happens to my emails after processing?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The email is used to extract the expense data and then it is discarded. We do not store the original email content. Only the structured data (vendor, amount, date) is kept in our secure database.",
                },
              },
              {
                "@type": "Question",
                name: "What types of expenses can you track?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Any email receipt with PDF attachments — Amazon purchases, Uber rides, restaurant bills, hotel bookings, office supplies, software subscriptions, and more. Works with Gmail, Outlook, Apple Mail, and all major email providers.",
                },
              },
              {
                "@type": "Question",
                name: "How accurate is the AI extraction?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Our AI is quite accurate for most common digital receipts, but you can easily edit any details that need correction. We're continuously improving accuracy, and you always have full control over your expense data.",
                },
              },
              {
                "@type": "Question",
                name: "How long does processing take?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Most receipts are processed within 1-3 minutes of forwarding the email. You'll see the extracted expense appear in your dashboard, where you can review and edit if needed.",
                },
              },
              {
                "@type": "Question",
                name: "What if something is extracted incorrectly?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No problem! You can easily edit any expense details directly from your dashboard. Think of the AI as a helpful first pass that gets you 90% of the way there, saving you from typing everything manually.",
                },
              },
              {
                "@type": "Question",
                name: "How is ForwardCents different from Expensify or SAP Concur?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Those tools are built for enterprises with complex approval workflows, team management, and corporate card integrations. ForwardCents is built for individuals — freelancers, consultants, and side hustlers who want simple, private expense tracking without connecting bank accounts or installing mobile apps.",
                },
              },
            ],
          }),
        }}
      />

      {/* Footer */}
      <footer className="border-t py-12 sm:py-16">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-4 h-4" />
                <span className="font-semibold">ForwardCents</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Privacy-first expense tracking. Built by an independent
                developer.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
                Product
              </h4>
              <div className="space-y-3">
                <a
                  href="/"
                  className="block text-sm text-foreground/70 hover:text-foreground transition-colors"
                >
                  Get Started
                </a>
                <a
                  href="/user/login"
                  className="block text-sm text-foreground/70 hover:text-foreground transition-colors"
                >
                  Sign In
                </a>
                <a
                  href="/help"
                  className="block text-sm text-foreground/70 hover:text-foreground transition-colors"
                >
                  Help
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
                Legal
              </h4>
              <div className="space-y-3">
                <a
                  href="/privacy"
                  className="block text-sm text-foreground/70 hover:text-foreground transition-colors"
                >
                  Privacy Policy
                </a>
                <a
                  href="/terms"
                  className="block text-sm text-foreground/70 hover:text-foreground transition-colors"
                >
                  Terms of Service
                </a>
                <a
                  href="/security"
                  className="block text-sm text-foreground/70 hover:text-foreground transition-colors"
                >
                  Security
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
                About
              </h4>
              <p className="text-sm text-foreground/70 leading-relaxed">
                Built with care for people who value their privacy.
              </p>
            </div>
          </div>
          <div className="pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} ForwardCents
            </p>
            <div className="flex items-center gap-6 text-xs text-muted-foreground">
              <a
                href="/privacy"
                className="hover:text-foreground transition-colors"
              >
                Privacy
              </a>
              <a
                href="/terms"
                className="hover:text-foreground transition-colors"
              >
                Terms
              </a>
              <a
                href="/security"
                className="hover:text-foreground transition-colors"
              >
                Security
              </a>
            </div>
          </div>
        </div>
      </footer>
    </AppLayout>
  );
}
